import {
  makeRational,
  parseRational,
  type Rational,
  toNumberR,
} from "@/lib/tool-kit/math/rational";

/**
 * Length units and the area and volume units built from them.
 *
 * Every factor is an exact rational number of metres (the inch is 0.0254 m, the foot
 * 0.3048 m, the yard 0.9144 m and the mile 1609.344 m by international agreement), so
 * a mixed-unit input such as 2 ft by 30 in is converted without a rounding error. An
 * area in a unit is the factor squared and a volume the factor cubed: 1 m² is
 * 10,000 cm², not 100.
 */

export const LENGTH_UNIT_IDS = [
  "mm",
  "cm",
  "m",
  "km",
  "in",
  "ft",
  "yd",
  "mi",
] as const;

export type LengthUnitId = (typeof LENGTH_UNIT_IDS)[number];

const exact = (text: string): Rational =>
  parseRational(text) ?? makeRational(BigInt(1));

/** Metres in one of each unit. */
export const METRES_PER_UNIT: Record<LengthUnitId, Rational> = {
  mm: exact("0.001"),
  cm: exact("0.01"),
  m: exact("1"),
  km: exact("1000"),
  in: exact("0.0254"),
  ft: exact("0.3048"),
  yd: exact("0.9144"),
  mi: exact("1609.344"),
};

/** Unit symbols: abbreviations, not prose, so they are the same in every language. */
export const LENGTH_SYMBOLS: Record<LengthUnitId, string> = {
  mm: "mm",
  cm: "cm",
  m: "m",
  km: "km",
  in: "in",
  ft: "ft",
  yd: "yd",
  mi: "mi",
};

export type Power = 1 | 2 | 3;

const SUPERSCRIPT: Record<Power, string> = { 1: "", 2: "²", 3: "³" };

/** "cm", "cm²", "cm³". */
export function unitSymbol(unit: LengthUnitId, power: Power): string {
  return `${LENGTH_SYMBOLS[unit]}${SUPERSCRIPT[power]}`;
}

/** A measurement converted between length units, exactly. */
export function convertLength(
  value: Rational,
  from: LengthUnitId,
  to: LengthUnitId
): Rational {
  if (from === to) {
    return value;
  }
  const ratio = makeRational(
    METRES_PER_UNIT[from].num * METRES_PER_UNIT[to].den,
    METRES_PER_UNIT[from].den * METRES_PER_UNIT[to].num
  );
  return makeRational(value.num * ratio.num, value.den * ratio.den);
}

/** Non-length units a result is also given in, as square or cubic metres. */
export const AREA_EXTRA_IDS = ["ha", "acre"] as const;
export const VOLUME_EXTRA_IDS = ["mL", "L", "gal", "floz"] as const;
export type AreaExtraId = (typeof AREA_EXTRA_IDS)[number];
export type VolumeExtraId = (typeof VOLUME_EXTRA_IDS)[number];

const SQUARE_METRES: Record<AreaExtraId, number> = {
  ha: 10_000,
  // 43,560 square feet of 0.3048 m
  acre: 4046.856_422_4,
};

/** US liquid measures: the gallon is 231 cubic inches, the fluid ounce 1/128 of it. */
const CUBIC_METRES: Record<VolumeExtraId, number> = {
  mL: 1e-6,
  L: 1e-3,
  gal: 0.003_785_411_784,
  floz: 0.000_029_573_529_562_5,
};

export const EXTRA_SYMBOLS: Record<AreaExtraId | VolumeExtraId, string> = {
  ha: "ha",
  acre: "ac",
  mL: "mL",
  L: "L",
  gal: "gal",
  floz: "fl oz",
};

export interface ConversionRow {
  id: string;
  symbol: string;
  value: number;
}

function metresPerUnit(unit: LengthUnitId): number {
  return toNumberR(METRES_PER_UNIT[unit]);
}

/**
 * The same area or volume in every unit: the value in `unit`², `unit`³ or `unit`, as a
 * float, turned into square or cubic metres and then divided by each target's factor
 * to the same power.
 */
export function conversionRows(
  value: number,
  unit: LengthUnitId,
  power: Power
): ConversionRow[] {
  const base = value * metresPerUnit(unit) ** power;
  const rows: ConversionRow[] = LENGTH_UNIT_IDS.map((id) => ({
    id,
    symbol: unitSymbol(id, power),
    value: base / metresPerUnit(id) ** power,
  }));
  if (power === 2) {
    for (const id of AREA_EXTRA_IDS) {
      rows.push({
        id,
        symbol: EXTRA_SYMBOLS[id],
        value: base / SQUARE_METRES[id],
      });
    }
  }
  if (power === 3) {
    for (const id of VOLUME_EXTRA_IDS) {
      rows.push({
        id,
        symbol: EXTRA_SYMBOLS[id],
        value: base / CUBIC_METRES[id],
      });
    }
  }
  return rows;
}
